import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ChevronLeft, Terminal, Sparkles, Download, Loader2, Plus, Trash2,
  Database, AlertCircle, CheckCircle2, Map, Layers,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.16ay — DARA Exploration Trial · self-serve "plug in your
// assays, see the brief live" flow. Built off the Complete Prospecting
// (P 59/2397) lease report Dayo handed over 2026-06-02.
//
// Anchored on the founder's directive: "What I want is for them to go to
// DARA, open a trial page, insert those information or their own
// information and get the brief live. That would sell fastly."
//
// Architectural promises preserved:
//   · No login. No friction.
//   · No DARA Engine vendor/library leaks.
//   · Every brief generation is logged server-side for founder visibility.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_HOLE = () => ({
  hole_id: "",
  total_depth: 0,
  depth_from: 0,
  depth_to: 0,
  width: 0,
  au_gpt: 0,
  notes: "",
});

const EMPTY_TENEMENT = {
  project_name: "",
  tenement_id: "",
  holder: "",
  jurisdiction: "",
  commodity: "Au",
  datum_grid: "",
  easting: "",
  northing: "",
  geological_setting: "",
  reporting_geologist: "",
};

export default function ExplorationTrial() {
  usePageview("/exploration-trial");

  const [tenement, setTenement] = useState(EMPTY_TENEMENT);
  const [holes, setHoles] = useState(() => Array.from({ length: 5 }, EMPTY_HOLE));
  const [cutoff, setCutoff] = useState(0.5);
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);

  // Auto-prefill on mount with the Complete Prospecting demo seed so the
  // founder / Dayo / Adegoke can land on the page and see something
  // ready to run.
  useEffect(() => {
    loadDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDemo = async () => {
    try {
      const { data } = await axios.get(`${API}/public/exploration-trial/demo`);
      setTenement(data.tenement);
      setHoles(data.holes);
      setCutoff(data.au_cutoff_gpt);
      setBrief(null);
      setError(null);
      toast.success("Demo dataset loaded — Complete Prospecting · P 59/2397");
    } catch {
      toast.error("Couldn't load the demo. Try again.");
    }
  };

  const clearAll = () => {
    setTenement(EMPTY_TENEMENT);
    setHoles(Array.from({ length: 5 }, EMPTY_HOLE));
    setBrief(null);
    setError(null);
  };

  const updateHole = (idx, field, value) => {
    setHoles((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: field === "hole_id" || field === "notes" ? value : Number(value) || 0 };
      // auto-compute width from depth_from/depth_to if both supplied
      if ((field === "depth_from" || field === "depth_to") && next[idx].depth_to > next[idx].depth_from) {
        next[idx].width = Number((next[idx].depth_to - next[idx].depth_from).toFixed(1));
      }
      return next;
    });
  };

  const addHole = () => {
    if (holes.length >= 20) {
      toast.error("Max 20 holes per trial brief — call us for larger datasets.");
      return;
    }
    setHoles([...holes, EMPTY_HOLE()]);
  };

  const removeHole = (idx) => {
    setHoles(holes.filter((_, i) => i !== idx));
  };

  const generate = async () => {
    const filledHoles = holes.filter((h) => h.hole_id.trim().length > 0);
    if (filledHoles.length === 0) {
      toast.error("Enter at least one hole with a hole ID.");
      return;
    }
    setLoading(true);
    setError(null);
    setBrief(null);
    try {
      const { data } = await axios.post(`${API}/public/exploration-trial/generate-brief`, {
        tenement,
        holes: filledHoles,
        au_cutoff_gpt: cutoff,
        visitor_session: getVisitorSession(),
      }, { timeout: 60000 });
      setBrief(data);
      // Scroll to the brief pane on mobile (no-op on desktop).
      setTimeout(() => {
        const el = document.getElementById("brief-output");
        if (el && window.innerWidth < 1024) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Brief generation failed. Try again or talk to the DARA team.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="exploration-trial-page">
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="trial-brand-home">
            <ChevronLeft className="w-4 h-4 text-zinc-500" />
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
            <span className="font-mono text-[10px] sm:text-[11px] text-zinc-500 ml-2 hidden sm:inline">// EXPLORATION TRIAL · LIVE BRIEF</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/dayo" className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white px-3 py-2 hidden sm:inline" data-testid="trial-nav-dayo">
              Exploration overview
            </Link>
            <Link to="/pricing" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors" data-testid="trial-nav-pricing">
              Talk to DARA team
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/30 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            // EXPLORATION TRIAL · NO LOGIN · NO CARD · NO PROCUREMENT GAUNTLET
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            Plug your overnight assays in. <span className="text-[#FFB000]">See your morning brief live in 60 seconds.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            We pre-loaded Complete Prospecting · P 59/2397 (Mt Magnet WA · Yilgarn greenstone · Au target) with 10 fabricated assays so you can see exactly how DARA synthesises a regulator-grade brief from drill data. <span className="text-[#FFB000]">Replace any row with your own numbers — or load yours from scratch — and re-run.</span>
          </p>
          <p className="mt-3 text-xs sm:text-sm text-zinc-500 max-w-3xl italic leading-relaxed">
            Your inputs stay on this page. Brief generations are logged server-side for our visibility only; we do not store your raw assay table in any tenant other than the trial buffer that expires within 7 days.
          </p>
        </div>
      </header>

      {/* MAIN — two-pane workspace */}
      <main className="border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* ─── LEFT: INPUT FORM ─── */}
          <section className="lg:col-span-5 space-y-6" data-testid="trial-input-pane">
            {/* Tenement card */}
            <div className="border border-zinc-900 bg-zinc-950/50 p-5 space-y-3">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-2">
                <Map className="w-3.5 h-3.5" /> // TENEMENT METADATA
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Project" value={tenement.project_name} onChange={(v) => setTenement({ ...tenement, project_name: v })} testId="t-project" />
                <Field label="Tenement ID" value={tenement.tenement_id} onChange={(v) => setTenement({ ...tenement, tenement_id: v })} testId="t-id" />
                <Field label="Holder" value={tenement.holder} onChange={(v) => setTenement({ ...tenement, holder: v })} testId="t-holder" />
                <Field label="Jurisdiction" value={tenement.jurisdiction} onChange={(v) => setTenement({ ...tenement, jurisdiction: v })} testId="t-jurisdiction" />
                <Field label="Commodity" value={tenement.commodity} onChange={(v) => setTenement({ ...tenement, commodity: v })} testId="t-commodity" />
                <Field label="Datum / Grid" value={tenement.datum_grid} onChange={(v) => setTenement({ ...tenement, datum_grid: v })} testId="t-datum" />
              </div>
              <Field
                label="Geological setting"
                multiline
                value={tenement.geological_setting}
                onChange={(v) => setTenement({ ...tenement, geological_setting: v })}
                testId="t-geo"
              />
            </div>

            {/* Holes table */}
            <div className="border border-zinc-900 bg-zinc-950/50 p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-2">
                  <Database className="w-3.5 h-3.5" /> // OVERNIGHT ASSAYS · {holes.length} HOLE{holes.length === 1 ? "" : "S"}
                </div>
                <button
                  onClick={addHole}
                  data-testid="trial-add-hole-btn"
                  className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] hover:text-white flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add hole
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-wider border-b border-zinc-800">
                      <th className="px-1 py-2 w-[18%]">Hole ID</th>
                      <th className="px-1 py-2 w-[10%]">Total m</th>
                      <th className="px-1 py-2 w-[10%]">From m</th>
                      <th className="px-1 py-2 w-[10%]">To m</th>
                      <th className="px-1 py-2 w-[10%]">Width m</th>
                      <th className="px-1 py-2 w-[12%]">Au g/t</th>
                      <th className="px-1 py-2 w-[25%]">Notes</th>
                      <th className="px-1 py-2 w-[5%]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {holes.map((h, idx) => (
                      <tr key={idx} data-testid={`trial-hole-row-${idx}`} className="border-b border-zinc-900">
                        <td className="px-1 py-1"><Cell value={h.hole_id} onChange={(v) => updateHole(idx, "hole_id", v)} placeholder="VRC001" /></td>
                        <td className="px-1 py-1"><Cell value={h.total_depth} onChange={(v) => updateHole(idx, "total_depth", v)} type="number" /></td>
                        <td className="px-1 py-1"><Cell value={h.depth_from} onChange={(v) => updateHole(idx, "depth_from", v)} type="number" /></td>
                        <td className="px-1 py-1"><Cell value={h.depth_to} onChange={(v) => updateHole(idx, "depth_to", v)} type="number" /></td>
                        <td className="px-1 py-1"><Cell value={h.width} onChange={(v) => updateHole(idx, "width", v)} type="number" /></td>
                        <td className="px-1 py-1"><Cell value={h.au_gpt} onChange={(v) => updateHole(idx, "au_gpt", v)} type="number" step="0.01" /></td>
                        <td className="px-1 py-1"><Cell value={h.notes} onChange={(v) => updateHole(idx, "notes", v)} placeholder="optional" /></td>
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => removeHole(idx)} className="text-zinc-600 hover:text-red-400" data-testid={`trial-remove-hole-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Cutoff slider */}
              <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center gap-3 flex-wrap">
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Cut-off Au g/t</label>
                <input
                  type="range" min="0" max="5" step="0.1" value={cutoff}
                  onChange={(e) => setCutoff(Number(e.target.value))}
                  className="flex-1 max-w-[200px]"
                  data-testid="trial-cutoff-slider"
                />
                <span className="font-mono text-sm text-[#FFB000] tabular-nums w-12" data-testid="trial-cutoff-value">
                  {cutoff.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3" data-testid="trial-actions">
              <button
                onClick={generate}
                disabled={loading}
                data-testid="trial-generate-btn"
                className="flex-1 sm:flex-none bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? "Synthesising brief…" : "Generate brief live"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <button onClick={loadDemo} data-testid="trial-load-demo-btn"
                className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 px-4 py-3">
                Load demo
              </button>
              <button onClick={clearAll} data-testid="trial-clear-btn"
                className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 border border-zinc-800 hover:border-zinc-600 px-4 py-3">
                Clear
              </button>
            </div>
          </section>

          {/* ─── RIGHT: BRIEF OUTPUT ─── */}
          <section className="lg:col-span-7" id="brief-output" data-testid="trial-output-pane">
            {!brief && !loading && !error && (
              <div className="border border-zinc-900 bg-zinc-950/30 p-8 sm:p-12 text-center min-h-[400px] flex flex-col items-center justify-center" data-testid="trial-empty-state">
                <Layers className="w-12 h-12 text-zinc-700 mb-4" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// AWAITING ASSAYS</div>
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Your live brief will appear here.</h3>
                <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
                  The DARA Engine reads your assay table, ranks intercepts by grade × width, applies JORC 2012 materiality, generates a synthesised anomaly heatmap, and writes the brief in plain English. ~10-20 seconds.
                </p>
              </div>
            )}

            {loading && (
              <div className="border border-zinc-900 bg-zinc-950/30 p-8 sm:p-12 text-center min-h-[400px] flex flex-col items-center justify-center" data-testid="trial-loading-state">
                <Loader2 className="w-10 h-10 text-[#FFB000] animate-spin mb-4" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// DARA ENGINE SYNTHESISING</div>
                <h3 className="font-heading font-bold text-lg tracking-tight mb-2">Ranking intercepts · applying JORC clause 19 · rendering heatmap…</h3>
                <p className="text-xs text-zinc-500 max-w-md leading-relaxed mt-2">
                  This is the real DARA Engine. Live API call, no mock. Hold tight.
                </p>
              </div>
            )}

            {error && !loading && (
              <div className="border border-red-900/50 bg-red-950/20 p-6" data-testid="trial-error-state">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-bold text-base mb-1 text-red-300">Brief generation failed</h3>
                    <p className="text-sm text-zinc-300 leading-relaxed">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {brief && !loading && (
              <BriefDisplay brief={brief} />
            )}
          </section>
        </div>
      </main>

      {/* FOOTER CTA */}
      <section className="border-t border-zinc-900 py-12 sm:py-16 bg-[#0a0a0a]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <FooterCard
            title="Like what you see?"
            body="This is the trial-grade flow. The full DARA workspace ingests your live drill database, geophysics, satellite, geochem, and competitor watch against your jurisdictional rulebook — and runs daily, not on-demand."
            cta="Talk to DARA team"
            href="/pricing"
            testId="trial-footer-talk"
          />
          <FooterCard
            title="More on DARA Exploration"
            body="Read the full exploration product page — six value-add layers, plain-English breakdowns, four honest gaps, and the partnership structure."
            cta="See /dayo"
            href="/dayo"
            testId="trial-footer-dayo"
          />
          <FooterCard
            title="Other verticals"
            body="DARA also serves wagering compliance, industrial telemetry synthesis, UK fire-safety, AU insurance channel, and energy arbitrage. One synthesis layer, seven operational realities."
            cta="See all verticals"
            href="/"
            testId="trial-footer-home"
          />
        </div>
      </section>

      <footer className="bg-black border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
          <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
            DARA OS · Perth WA · daraos.ai · This trial output is illustrative and not a substitute for a JORC competent-person sign-off.
          </p>
        </div>
      </footer>
    </div>
  );
}


// ─── SUB-COMPONENTS ─────────────────────────────────────────────────────
function Field({ label, value, onChange, multiline, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">{label}</label>
      {multiline ? (
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-zinc-950 border border-zinc-800 text-white px-3 py-2 font-mono text-xs focus:outline-none focus:border-[#FFB000]"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-zinc-950 border border-zinc-800 text-white px-3 py-2 font-mono text-xs focus:outline-none focus:border-[#FFB000]"
        />
      )}
    </div>
  );
}

function Cell({ value, onChange, type = "text", placeholder, step }) {
  return (
    <input
      type={type}
      value={value}
      step={step}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-zinc-950 border border-zinc-800 text-white px-2 py-1.5 font-mono text-xs focus:outline-none focus:border-[#FFB000]"
    />
  );
}

function BriefDisplay({ brief }) {
  const { stats, brief: prose, tenement, pdf_url, brief_id } = brief;
  const pdfFullUrl = pdf_url ? `${BACKEND_URL}${pdf_url}` : null;

  return (
    <div className="space-y-5" data-testid="trial-brief-display">
      {/* HEADER STRIP */}
      <div className="border border-zinc-900 bg-black p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">
          // DARA TRIAL BRIEF · {tenement.project_name || "TENEMENT"} · GENERATED LIVE
        </div>
        <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tight leading-tight" data-testid="brief-headline">
          {prose.headline}
        </h2>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-zinc-400">
          {tenement.tenement_id && <span>{tenement.tenement_id}</span>}
          {tenement.holder && <span>· {tenement.holder}</span>}
          {tenement.jurisdiction && <span>· {tenement.jurisdiction}</span>}
          {tenement.commodity && <span>· {tenement.commodity}</span>}
        </div>
      </div>

      {/* STAT STRIP */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="brief-stat-strip">
        <StatTile label="Holes" value={stats.hole_count} tone="white" />
        <StatTile label="Material" value={stats.material_count} tone={stats.material_count > 0 ? "amber" : "muted"} />
        <StatTile label="Mineralised m" value={stats.total_mineralised_m} tone="white" />
        <StatTile label="Avg Au g/t" value={stats.avg_grade_above_cutoff?.toFixed(2) || "0.00"} tone="amber" />
      </div>

      {/* MATERIALITY ALERT */}
      <div className={`border p-4 ${stats.materiality_flag ? "border-red-900/50 bg-red-950/20" : "border-zinc-800 bg-zinc-950/30"}`} data-testid="brief-materiality">
        <div className={`font-mono text-[10px] uppercase tracking-widest mb-2 flex items-center gap-2 ${stats.materiality_flag ? "text-red-400" : "text-zinc-400"}`}>
          {stats.materiality_flag ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {stats.materiality_flag ? "// MATERIALITY · JORC 2012 CLAUSE 19" : "// MATERIALITY · BELOW THRESHOLD"}
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed">{prose.materiality}</p>
      </div>

      {/* TOP INTERCEPTS TABLE */}
      <div className="border border-zinc-900 bg-zinc-950/30 p-5" data-testid="brief-top-intercepts">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
          // TOP RANKED INTERCEPTS · BY GRADE × WIDTH
        </div>
        {stats.top_intercepts.length === 0 ? (
          <p className="text-xs text-zinc-500 italic">No intercepts above cutoff at the supplied threshold.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-wider border-b border-zinc-800">
                <th className="px-2 py-2">Hole</th>
                <th className="px-2 py-2">Intercept</th>
                <th className="px-2 py-2">Au g/t</th>
                <th className="px-2 py-2">From</th>
                <th className="px-2 py-2">G×W</th>
                <th className="px-2 py-2">Flag</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_intercepts.map((t, i) => (
                <tr key={i} className="border-b border-zinc-900">
                  <td className="px-2 py-2 font-mono font-bold text-white">{t.hole_id}</td>
                  <td className="px-2 py-2 text-zinc-300">{t.width}m</td>
                  <td className="px-2 py-2 text-[#FFB000] font-bold">{t.au_gpt.toFixed(2)}</td>
                  <td className="px-2 py-2 text-zinc-300">{t.depth_from}m</td>
                  <td className="px-2 py-2 text-zinc-300 font-mono">{t.grade_thickness.toFixed(1)}</td>
                  <td className="px-2 py-2">
                    <span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${t.material ? "bg-red-900/30 text-red-300 border border-red-800" : "bg-amber-900/20 text-amber-300 border border-amber-900"}`}>
                      {t.material ? "MATERIAL" : "WATCH"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* NEXT 5 DRILL HOLES */}
      <div className="border border-zinc-900 bg-zinc-950/30 p-5" data-testid="brief-next-drill">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
          // NEXT 5 DRILL HOLES · RANKED CAPITAL ALLOCATION
        </div>
        <div className="space-y-2 text-sm text-zinc-200 leading-relaxed whitespace-pre-line">
          {prose.next_drill_rec}
        </div>
      </div>

      {/* CAPITAL + WATCH (2-col) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-zinc-900 bg-zinc-950/30 p-5" data-testid="brief-capital">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // CAPITAL ALLOCATION READ
          </div>
          <p className="text-sm text-zinc-200 leading-relaxed">{prose.capital_allocation}</p>
        </div>
        <div className="border border-zinc-900 bg-zinc-950/30 p-5" data-testid="brief-watch">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // 14-DAY WATCHLIST
          </div>
          <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{prose.watch}</div>
        </div>
      </div>

      {/* DOWNLOAD PDF */}
      {pdfFullUrl && (
        <div className="border border-[#FFB000]/30 bg-[#0f0b00] p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between" data-testid="brief-pdf-cta">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">// PDF DOWNLOAD READY</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Forward the brief to your principal geologist or MD. Two-page A4 with the heatmap, intercept ranking, next-drill rec, capital read, and 14-day watchlist.
            </p>
          </div>
          <a
            href={pdfFullUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="brief-pdf-download"
            className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </a>
        </div>
      )}

      {/* IDENTITY HASH */}
      <div className="text-center pt-2">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          // brief id · {brief_id?.slice(0, 12)} · regulator-grade evidence chain enabled in the full workspace
        </p>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const valueColor = tone === "amber" ? "text-[#FFB000]" : tone === "muted" ? "text-zinc-500" : "text-white";
  return (
    <div className="bg-black p-3 sm:p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
    </div>
  );
}

function FooterCard({ title, body, cta, href, testId }) {
  return (
    <Link
      to={href}
      data-testid={testId}
      className="block border border-zinc-900 bg-black p-5 hover:bg-zinc-950 transition-colors group"
    >
      <h3 className="font-heading font-bold text-base tracking-tight mb-2 group-hover:text-[#FFB000] transition-colors">{title}</h3>
      <p className="text-xs text-zinc-400 leading-relaxed mb-4">{body}</p>
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000] flex items-center gap-1.5">
        {cta} <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}
